import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/Modal";
import { Input } from "@/components/Input";
import { Select, SelectOption } from "@/components/Select";
import { useToast } from "@/context/ToastContext";
import { clientApi } from "@/lib/api";
import { Client } from "@/pages/Tenders/types";

interface QuickClientModalProps {
    isOpen: boolean;
    onClose: () => void;
    onClientCreated: (client: Client) => void;
}

export const QuickClientModal: React.FC<QuickClientModalProps> = ({
    isOpen,
    onClose,
    onClientCreated,
}) => {
    const { t } = useTranslation("global");
    const { showSuccess, showError } = useToast();

    const [name, setName] = useState<string>("");
    const [taxId, setTaxId] = useState<string>("");
    const [phone, setPhone] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [city, setCity] = useState<string>("");
    const [countryId, setCountryId] = useState<number | null>(66);
    const [provinceId, setProvinceId] = useState<number | null>(null);
    const [countries, setCountries] = useState<any[]>([]);
    const [provinces, setProvinces] = useState<any[]>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    useEffect(() => {
        if (isOpen) {
            clientApi.countries().then((res) => {
                const list = res?.data || [];
                setCountries(list);
                const spain = list.find(
                    (c: any) => c.is_spain || c.name.toLowerCase() === "españa"
                );
                if (spain) {
                    setCountryId(spain.id);
                }
            });
            clientApi.provinces().then((res) => setProvinces(res?.data || []));
        }
    }, [isOpen]);

    const countryOptions: SelectOption<number>[] = countries.map((c: any) => ({
        value: c.id,
        label: c.name,
    }));

    const provinceOptions: SelectOption<number>[] = provinces.map((p: any) => ({
        value: p.id,
        label: p.name,
    }));

    const selectedCountry = countries.find((c: any) => c.id === countryId);
    const isSpain: boolean = selectedCountry
        ? Boolean(
              selectedCountry.is_spain ||
                  selectedCountry.name.toLowerCase() === "españa"
          )
        : countryId === 66;

    const resetForm = (): void => {
        setName("");
        setTaxId("");
        setPhone("");
        setEmail("");
        setCity("");
        setProvinceId(null);
        const spain = countries.find(
            (c: any) => c.is_spain || c.name.toLowerCase() === "españa"
        );
        setCountryId(spain ? spain.id : 66);
    };

    const handleSubmit = async (): Promise<void> => {
        if (!name.trim()) {
            return;
        }

        setIsSubmitting(true);
        try {
            const res = await clientApi.quickCreate({
                name: name.trim(),
                tax_id: taxId.trim() || null,
                phone: phone.trim() || null,
                email: email.trim() || null,
                city: city.trim() || null,
                country_id: countryId ?? null,
                province_id: isSpain ? (provinceId ?? null) : null,
                is_active: true,
            });

            if (res?.data) {
                showSuccess(t("tender.client_saved_success"));
                onClientCreated(res.data);
                resetForm();
                onClose();
            }
        } catch (error) {
            console.error(error);
            showError(t("common.error"));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t("tender.quick_client_title")}
            maxWidth="md"
            onSubmit={handleSubmit}
            submitLabel={t("tender.save")}
            cancelLabel={t("tender.cancel")}
            isSubmitting={isSubmitting}
            submitDisabled={!name.trim()}
        >
            <div className="space-y-4 py-2">
                <Input
                    label={t("clients.name")}
                    value={name}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setName(e.target.value)
                    }
                    required
                />

                <Input
                    label={t("clients.tax_id")}
                    value={taxId}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setTaxId(e.target.value)
                    }
                />
                <Input
                    label={t("clients.phone")}
                    value={phone}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setPhone(e.target.value)
                    }
                />

                <Input
                    label={t("clients.email")}
                    type="email"
                    value={email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setEmail(e.target.value)
                    }
                />

                <Input
                    label={t("clients.city")}
                    value={city}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setCity(e.target.value)
                    }
                />

                <Select<number>
                    label={t("clients.country")}
                    value={countryId ?? 0}
                    onChange={(val: number) => {
                        const newId = val || null;
                        setCountryId(newId);
                        const c = countries.find(
                            (item: any) => item.id === newId
                        );
                        if (
                            !c ||
                            !(c.is_spain || c.name.toLowerCase() === "españa")
                        ) {
                            setProvinceId(null);
                        }
                    }}
                    options={countryOptions}
                    placeholder={t("clients.select_country")}
                    searchable
                    clearable
                    full
                />

                {isSpain && (
                    <Select<number>
                        label={t("clients.province")}
                        value={provinceId ?? 0}
                        onChange={(val: number) => setProvinceId(val || null)}
                        options={provinceOptions}
                        placeholder={t("clients.select_province")}
                        searchable
                        clearable
                        full
                    />
                )}
            </div>
        </Modal>
    );
};
